import { Injectable, inject } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';

/**
 * Service responsible for providing form validation functionality across the application.
 * Contains reusable validation patterns and error messages for various form fields.
 */
@Injectable({
	providedIn: 'root',
})
export class ValidationService {
	// Regular expression patterns for different types of validations
	private readonly emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
	private readonly passwordPattern = /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!@#$%^&*]).{8,}$/;

	private readonly transloco = inject(TranslocoService);

	/**
	 * Maps each validator error name to its i18n key under the `validation.*` namespace.
	 */
	private readonly messageKeys: { [key: string]: string } = {
		required: 'validation.required',
		min: 'validation.minGreaterThanZero',
		invalidLogin: 'validation.invalidLogin',
		invalidEmailAddress: 'validation.email',
		email: 'validation.email',
		invalidPassword: 'validation.password',
		passwordsDoNotMatch: 'validation.passwordsDoNotMatch',
		termsRequired: 'validation.termsRequired',
		pepRequired: 'validation.pepRequired',
		invalidNIF: 'validation.invalidNif',
		invalidCIF: 'validation.invalidCif',
		invalidPhone: 'validation.invalidPhone',
		invalidCardNumber: 'validation.invalidCardNumber',
		invalidExpirationDate: 'validation.invalidExpirationDate',
		invalidCVC: 'validation.invalidCvc',
		invalidPostalCode: 'validation.invalidPostalCode',
		integer: 'validation.integer',
		invalidNumber: 'validation.invalidNumber',
	};

	/**
	 * Retrieves the translated error message for a given validator name.
	 * @param validatorName - The name of the validator
	 * @returns The corresponding message in the active language, or a default message if not found
	 */
	getValidatorErrorMessage(validatorName: string): string {
		const key = this.messageKeys[validatorName];
		return this.transloco.translate(key ?? 'validation.unknown');
	}

	/**
	 * Validates email format using a regular expression pattern.
	 * @returns A validator function that checks if the email format is valid
	 */
	emailValidator() {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			if (value && !this.emailPattern.test(value)) {
				return { invalidEmailAddress: true };
			}
			return null;
		};
	}

	/**
	 * Validates password strength using a regular expression pattern.
	 * Password must contain at least:
	 * - One uppercase letter
	 * - One lowercase letter
	 * - One number
	 * - One special character
	 * - Minimum 8 characters
	 * @returns A validator function that checks if the password meets the requirements
	 */
	passwordValidator() {
		return (control: AbstractControl): { [key: string]: boolean } | null => {
			const value = control.value;
			// If the field is empty, don't validate here (let 'required' handle it)
			if (!value || value === '') {
				return null;
			}
			// Validate the pattern only if there is a value
			if (this.passwordPattern.test(value)) {
				return null; // Valid
			}
			return { invalidPassword: true }; // Invalid
		};
	}

	/**
	 * Retrieves tooltip messages for password requirements.
	 * @param key - The key identifying the tooltip message
	 * @returns The formatted HTML message for the tooltip
	 */
	getTooltipMessage(key: string): string {
		if (key !== 'invalidPassword') return '';
		const t = (k: string) => this.transloco.translate(`validation.passwordTooltip.${k}`);
		return `
			<div class="password-requirements">
				<div class="requirements-title">${t('title')}</div>
				<ul>
					<li>${t('min8')}</li>
					<li>${t('uppercase')}</li>
					<li>${t('lowercase')}</li>
					<li>${t('number')}</li>
					<li>${t('special')}</li>
				</ul>
			</div>
		`;
	}

	/**
	 * Validates that two password fields match.
	 * @param passwordFieldName - The name of the password field to compare against
	 * @returns A validator function that checks if the passwords match
	 */
	confirmPasswordValidator(passwordFieldName: string) {
		return (control: AbstractControl): { [key: string]: boolean } | null => {
			const formGroup = control.parent;
			if (formGroup) {
				const passwordControl = formGroup.get(passwordFieldName);
				if (passwordControl && passwordControl.value !== control.value) {
					return { passwordsDoNotMatch: true };
				}
			}
			return null;
		};
	}

	/**
	 * Validates that a value is an integer.
	 * @returns A validator function that checks if the value is an integer
	 */
	static integerValidator(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			return !Number.isInteger(+value) ? { integer: true } : null;
		};
	}
}
